import {
  AuthResponse,
  BookDiplomaRequestInput,
  BookDiplomaRequestResponse,
  BookExamResponse,
  BookExamUserInput,
  BookLiveCorrectionInput,
  BookMockTestInput,
  BookOneToOneInput,
  BookOneToOneOutput,
  BookSpeakingClassesInput,
  BookedIELTS,
  CalendarListParams,
  CalenderItem,
  ClassReports,
  CorrectExerciseInput,
  CorrectExerciseResponse,
  DefaultResponse,
  DownloadInvoiceInput,
  DownloadInvoiceResponse,
  EventMaterials,
  ExerciseProgressRequest,
  ExerciseStatusRequest,
  FeedbackInput,
  FeedbackRatingInput,
  FeedbackRatingResponse,
  FeedbackResponse,
  FindUser,
  GetChangedEventInput,
  GetChangedEventResponse,
  GetChangedEventsInput,
  GetChangedEventsResponse,
  LoginActivity,
  LoginUserInput,
  NewPasswordUserInput,
  NewPasswordBetaPayload,
  NewTutorAvailabilityItem,
  OneToOneCredits,
  PasswordResetResponse,
  PasswordResetUserInput,
  ReferralResponse,
  RegisterUserInput,
  ScheduleSpeakingClassesAvailable,
  SetChangeEventInput,
  SetChangeEventResponse,
  SpeakingClassesAvailable,
  TutorAvailabilityItem,
  TutorAvailabilityParams,
  TutorOneToOneAvailabilityItem,
  TutorOneToOneAvailabilityParams,
  UpdateUserAvailabilityInput,
  UpsellingRequest,
  User,
  UserAvailabilities,
  UserExercises,
  UserIniPayments,
  UserNotificationPaginator,
  UserOnboardingSaveInput,
  UserOneToOneCredits,
  UserOneToOneTopicsDone,
  UserProducts,
  UserReports,
  VerifyPasswordTokenUserInputResponse,
  UpdateTimezoneInput,
} from "@/types";
import API_ENDPOINTS from "./endpoints";
import { HttpClient } from "./http-client";
import axios from "axios";

class Client {
  users = {
    me: () => HttpClient.get<User>(API_ENDPOINTS.USERS_ME, {}),
    updateTimezone: (input: UpdateTimezoneInput) => {
      return axios.post<DefaultResponse>(
        API_ENDPOINTS.USERS_UPDATE_TIMEZONE,
        input,
      );
    },
    find: (token: string) =>
      HttpClient.get<FindUser>(API_ENDPOINTS.FIND_USER(token), {}),
    availabilities: () =>
      HttpClient.get<UserAvailabilities>(API_ENDPOINTS.AVAILABILITIES, {}),
    iniPayments: () =>
      HttpClient.get<UserIniPayments>(API_ENDPOINTS.PAYMENTS, {}),
    products: () =>
      HttpClient.get<UserProducts>(API_ENDPOINTS.USER_PRODUCTS, {}),
    oneToOneCredits: () =>
      HttpClient.get<OneToOneCredits>(API_ENDPOINTS.ONE_TO_ONE_CREDITS, {}),
    notifications: () =>
      HttpClient.get<UserNotificationPaginator>(
        API_ENDPOINTS.NOTIFICATIONS,
        {},
      ),
    setNotificationMetadataAsSeen: (
      inputs:
        | { id_notification: number; id_metadata: number }
        | { token_user: string },
    ) => HttpClient.post<void>(API_ENDPOINTS.NOTIFICATIONS_SET_SEEN, inputs),
    exercises: () =>
      HttpClient.get<UserExercises>(API_ENDPOINTS.USER_EXERCISES, {}),
    exercises_pot: () =>
      HttpClient.get<UserExercises>(API_ENDPOINTS.USER_EXERCISES_POT, {}),
    getChangeEvents: (input: GetChangedEventsInput) =>
      HttpClient.post<GetChangedEventsResponse>(
        API_ENDPOINTS.GET_CHANGED_EVENTS,
        input,
      ),
    eventMaterials: () =>
      HttpClient.get<EventMaterials>(API_ENDPOINTS.EVENTS_AND_MATERIALS),
    getChangeEvent: (input: GetChangedEventInput) =>
      HttpClient.post<GetChangedEventResponse>(
        API_ENDPOINTS.GET_CHANGE_EVENT,
        input,
      ),
    setChangeEvents: (input: SetChangeEventInput) =>
      HttpClient.post<SetChangeEventResponse>(
        API_ENDPOINTS.SET_CHANGED_EVENTS,
        input,
      ),
    onboarding: (input: UserOnboardingSaveInput) =>
      HttpClient.post<DefaultResponse>(
        API_ENDPOINTS.USER_SAVE_ONBOARDING,
        input,
      ),
    login: (input: LoginUserInput) =>
      HttpClient.post<AuthResponse>(API_ENDPOINTS.USERS_LOGIN, input),
    register: ({ token, ...rest }: RegisterUserInput) =>
      HttpClient.post<AuthResponse>(
        API_ENDPOINTS.USERS_REGISTRATION(token),
        rest,
      ),
    resetPassword: (input: PasswordResetUserInput) =>
      HttpClient.post<PasswordResetResponse>(
        API_ENDPOINTS.USERS_FORGOT_PASSWORD,
        input,
      ),
    newPassword: ({ password, token }: NewPasswordUserInput) =>
      HttpClient.post<DefaultResponse>(API_ENDPOINTS.USERS_RESET_PASSWORD, {
        password,
        token,
      }),
      resetPasswordBeta: (input: PasswordResetUserInput) =>
        HttpClient.post<PasswordResetResponse>(
          API_ENDPOINTS.USERS_FORGOT_PASSWORD_BETA,
          input,
        ),
      newPasswordBeta: ({ password, token, }: NewPasswordBetaPayload) =>
        HttpClient.post<DefaultResponse>(API_ENDPOINTS.USERS_RESET_PASSWORD_BETA, {
          password,
          token,
        }),
    referrals: () =>
      HttpClient.get<ReferralResponse>(API_ENDPOINTS.GET_USER_REFERRALS, {}),
    logout: () => HttpClient.post<boolean>(API_ENDPOINTS.USERS_LOGOUT, {}),
    loginActivity: (token: string) =>
      HttpClient.get<LoginActivity>(API_ENDPOINTS.USER_LOGIN_ACTIVITY(token)),
    verifyPasswordToken: (token: string) =>
      HttpClient.get<VerifyPasswordTokenUserInputResponse>(API_ENDPOINTS.USERS_PASSWORD_TOKEN_VERIFICATION(token)),
  };

  agenda = {
    setRecording: ({ user, id }: { user: string; id: number }) =>
      HttpClient.post<{ status: boolean; message?: string }>(
        API_ENDPOINTS.SET_RECORDING(id),
        {
          user,
        },
      ),

    upsellingTracks: ({ response }: UpsellingRequest) =>
      HttpClient.post<DefaultResponse>(API_ENDPOINTS.UPSELLING_TRACK, {
        response,
      }),
    getRecording: (id: number) =>
      HttpClient.post<{ link: string }>(API_ENDPOINTS.GET_RECORDING(id), {}),

    deleteEvent: (id: number, token: string) =>
      HttpClient.delete<{
        status: number;
        message: string;
        item: string;
      }>(API_ENDPOINTS.DELETE_EVENT(id), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
  };

  feedback = {
    createAgendaFeedback: (input: FeedbackInput) =>
      HttpClient.post<FeedbackResponse>(API_ENDPOINTS.CREATE_FEEDBACK, input),
    createFeedbackRating: (input: FeedbackRatingInput) =>
      HttpClient.post<FeedbackRatingResponse>(
        API_ENDPOINTS.FEEDBACK_RATINGS,
        input,
      ),
  };

  exercise = {
    correctExercise: ({ id, ...input }: CorrectExerciseInput) =>
      HttpClient.post<CorrectExerciseResponse[]>(
        API_ENDPOINTS.CORRECT_EXERCISE(id),
        input,
      ),
    bookMockTest: (input: BookMockTestInput) =>
      HttpClient.post<{
        status: number;
        message: string;
      }>(API_ENDPOINTS.BOOK_MOCK_TEST, input),

    updateStatus: (input: ExerciseStatusRequest) =>
      HttpClient.post<DefaultResponse>(API_ENDPOINTS.EXERCISE_STATUS, input),
    saveProgress: (input: ExerciseProgressRequest) =>
      HttpClient.post<DefaultResponse>(API_ENDPOINTS.EXERCISE_SAVE, input),
    checkBookLiveCorrection: () =>
      HttpClient.post<{
        status: number;
        message: string;
      }>(API_ENDPOINTS.CHECK_BOOK_LIVE_CORRECTION, {}),
    bookLiveCorrection: (input: BookLiveCorrectionInput) =>
      HttpClient.post<{
        status: number;
        message: string;
      }>(API_ENDPOINTS.BOOK_LIVE_CORRECTION, input),
  };

  ielts = {
    bookedIELTS: () =>
      HttpClient.get<BookedIELTS>(API_ENDPOINTS.BOOKED_IELTS, {}),
    bookIELTS: (input: BookExamUserInput) =>
      HttpClient.post<BookExamResponse>(API_ENDPOINTS.BOOK_IELTS, input),
    uploadPicBookIELTS: (input: { user_id: number; booking_pic: File }) =>
      HttpClient.post<DefaultResponse>(
        API_ENDPOINTS.UPLOAD_PIC_BOOK_IELTS,
        input,
        { headers: { "Content-Type": "multipart/form-data" } },
      ),
    removeBookIELTS: (id: number) =>
      HttpClient.delete<DefaultResponse>(
        API_ENDPOINTS.REMOVE_BOOK_IELTS(id),
        {},
      ),
  };

  invoice = {
    download: (input: DownloadInvoiceInput) =>
      HttpClient.post<DownloadInvoiceResponse>(
        API_ENDPOINTS.DOWNLOAD_INVOICE,
        input,
      ),
  };

  reports = {
    classReports: () =>
      HttpClient.get<ClassReports>(API_ENDPOINTS.CLASS_REPORTS, {}),
    getUserReports: (userId: string, eventId: string) =>
      HttpClient.post<UserReports>(
        `${API_ENDPOINTS.USER_REPORTS}?userId=${userId}&eventId=${eventId}`,
        {},
      ),
  };

  diploma = {
    calendarList: (input: Partial<CalendarListParams>) =>
      HttpClient.post<CalenderItem[]>(API_ENDPOINTS.CALENDAR_LIST, input),
    bookRequest: (input: BookDiplomaRequestInput) =>
      HttpClient.post<BookDiplomaRequestResponse>(
        API_ENDPOINTS.BOOK_DIPLOMA_REQUEST,
        input,
      ),
  };

  speakingClass = {
    speakingTutorAndTopicList: () =>
      HttpClient.get<SpeakingClassesAvailable>(
        API_ENDPOINTS.SPEAKING_CLASS_TUTOR_AND_TOPIC_LIST,
      ),
    bookSpeakingClass: (input: BookSpeakingClassesInput) => {
      return HttpClient.post<{ item: boolean }>(
        API_ENDPOINTS.SPEAKING_CLASS_TUTOR_AND_TOPIC_LIST,
        input,
        { timeout: 1000000 },
      );
    },
  };

  // schedule speaking class
  scheduleSpeakingClasses = {
    getScheduleSpeakingClasses: (currentPage: number) =>
      // HttpClient.get<SpeakingClassesAvailable>(
      HttpClient.get<ScheduleSpeakingClassesAvailable>(
        `${API_ENDPOINTS.SPEAKING_CLASS_TUTOR_AND_TOPIC_LIST}?page=${currentPage}`,
      ),
    createScheduleSpeakingClass: (input: BookSpeakingClassesInput) => {
      return HttpClient.post<{ item: boolean }>(
        API_ENDPOINTS.SPEAKING_CLASS_TUTOR_SCHEDULE,
        input,
        { timeout: 1000000 },
      );
    },
  };

  oneToOne = {
    tutorAvailabilities: (input: Partial<TutorAvailabilityParams>) =>
      HttpClient.post<TutorAvailabilityItem>(
        API_ENDPOINTS.AVAILABILITIES_TUTOR,
        input,
      ),
    tutorOneToOneAvailabilities: (
      input: Partial<TutorOneToOneAvailabilityParams>,
    ) =>
      HttpClient.post<TutorOneToOneAvailabilityItem>(
        API_ENDPOINTS.AVAILABILITIES_TUTOR_ONE_TO_ONE,
        input,
      ),
    newTutorOneToOneAvailabilities: (
      input: Partial<TutorOneToOneAvailabilityParams>,
    ) =>
      HttpClient.post<NewTutorAvailabilityItem>(
        API_ENDPOINTS.AVAILABILITIES_TUTOR_ALL,
        input,
      ),
    userCredits: () =>
      HttpClient.get<UserOneToOneCredits>(API_ENDPOINTS.ONE_TO_ONE_CREDITS, {}),
    book: (input: BookOneToOneInput) =>
      HttpClient.post<BookOneToOneOutput>(API_ENDPOINTS.BOOK_ONE_TO_ONE, input),
    getTopics: () =>
      HttpClient.get<UserOneToOneTopicsDone>(
        API_ENDPOINTS.ONE_TO_ONE_TOPICS_DONE,
      ),
  };

  availability = {
    update: (input: UpdateUserAvailabilityInput) =>
      HttpClient.post<string[]>(API_ENDPOINTS.UPDATE_USER_AVAILABILITY, input),
  };

  file = {
    download: ({
      id,
      type = "media",
    }: {
      id: string | number;
      type?: string;
    }) => {
      return HttpClient.post<Blob>(
        API_ENDPOINTS.GET_FILE(id),
        { type },
        { responseType: "blob" },
      );
    },
  };

  registration = {
    getIFrameSpeechAce: (user_token: string) =>
      HttpClient.post<{ status: boolean; data: string }>(
        API_ENDPOINTS.GENERATE_I_FRAME_SPEECHACE,
        { user_token },
      ),
    getUserScoreSpeechAce: (user_token: string) =>
      HttpClient.post<{ status: boolean; data: object }>(
        API_ENDPOINTS.GET_USER_SCORE_SPEECHACE,
        { user_token },
      ),
  };
}

const RequestClient = new Client();
export default RequestClient;
